import { useSourceStore } from "@/lib/source-store"

/**
 * Ad Creative Tier Detection
 *
 * Returns the ad tier to request from the API. The admin's global max-tier
 * cap is still enforced server-side in /api/ads/network-grid-ad regardless
 * of what's requested here — this is a client-side floor on top of that,
 * not a replacement for it.
 *
 * 1 = Mainstream, 2 = Softcore Adult, 3 = Explicit Adult (see
 * ad_zones.creative_tier).
 */

export type AdTier = 1 | 2 | 3

/**
 * Returns the ad tier to pass to the ad API, based on which sources are
 * actually contributing the posts currently on screen. Sources are only
 * ever tier-capped when EVERY site behind these posts is marked
 * contentRating: "safe" in lib/source-store.ts (SFW-only-by-design sources
 * like Safebooru, Sakugabooru, Zerochan, e926) — a mixed selection (even
 * one NSFW-capable source alongside a safe one) still gets the max tier,
 * since "safe" is a guarantee about the source, not a per-post filter.
 * Call with no posts (or an unrecognized site) to keep the previous
 * behavior of always requesting the max tier.
 */
export function useAdTier(posts?: Array<{ site?: string }>): AdTier {
  if (posts && posts.length > 0) {
    const sites = Array.from(new Set(posts.map((p) => p.site).filter((s): s is string => !!s)))
    if (sites.length > 0) {
      const { getSource } = useSourceStore.getState()
      const allSafe = sites.every((site) => getSource(site)?.contentRating === "safe")
      if (allSafe) return 1
    }
  }
  if (posts) return 3
  // No posts argument at all: use the tier the browse grid / open modal last published (3 until one has).
  return modalAdTier
}

export function getAdTier(): AdTier {
  return 3
}

/**
 * Tier for ads shown inside the open booru modal (album ad tiles, preroll, letterbox/pillarbox bars).
 * The modal sets it while rendering from the post being viewed, so nested ad components can read it
 * without prop drilling. Same rule as useAdTier: 1 only when the post's source is "safe".
 */
let modalAdTier: AdTier = 3
export function setModalAdTier(tier: AdTier) {
  modalAdTier = tier
}
export function getModalAdTier(): AdTier {
  return modalAdTier
}
